<template>
  <div :id="`${source}_prompt_phone_number`" class="px-3">
    <span v-if="row?.phone_number" class="text-sm">{{ row.phone_number }}</span>

    <a-button
      v-else
      class="secondary_medium"
      @click="accessPhoneNumber"
      :loading="isEnriching"
    >
      <ThemeImage
        v-if="!isEnriching"
        :icon="imageNames.PROSPECT_SEARCH_ICON"
        alt="prospect_search_icon"
        class="size-4"
      />
      {{ buttonText }}
    </a-button>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  onBeforeUnmount,
  watch,
} from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import eventBus from "@/src/utils/eventBus.js";
import { quickActionsTypes } from "@/src/constants/componentConstants.js";
import { enrichWithApollo } from "@/src/utils/enrichUtils.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { taskStatusTypes } from "@/src/constants/taskConstants.js";
import { showAlreadyExistsMessage } from "@/src/utils/utils.js";
import { handleApiError } from "@/src/utils/errorUtils.js";

const props = defineProps({
  source: { type: String, required: true, default: "custom_table" },
  row: { type: Object, required: true },
});

const isEnriching = ref(false);
const entity = ref(null);
const requestID = ref(null);
const pollingAttempts = ref(0);
const pollingInterval = ref(null);
const notFound = ref(false);
const actionKey = ref("phone_number");

const buttonText = computed(() => {
  return isEnriching.value ? "Accessing..." : "Access Phone Number";
});

const enrichingEventName = computed(() => `call-enriching-${props.row.id}`);

const setEnrichingState = (state) => {
  isEnriching.value = state;
  eventBus.emit(enrichingEventName.value, state);
};

const accessPhoneNumber = async () => {
  if (!props.row?.lead_id && !props.row?.contact_id) {
    await addToLeads();
  } else {
    await enrichEntity();
  }
};

const startPolling = () => {
  pollingAttempts.value = 0;
  stopPolling();

  pollingInterval.value = setInterval(async () => {
    pollingAttempts.value++;
    await fetchRequestStatus();

    if (pollingAttempts.value >= 20) {
      stopPolling();
      setEnrichingState(false);
      notFound.value = true;
    }
  }, 15000);
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
  pollingAttempts.value = 0;
};

const handleRequestStatus = (response, error = null) => {
  if (error) {
    console.error("Error fetching request status:", error);
    setEnrichingState(false);
    notFound.value = true;
    stopPolling();
    return;
  }

  const status = response?.request?.status;

  if (status === taskStatusTypes.PENDING) {
    setEnrichingState(true);
    return;
  }

  if (status === taskStatusTypes.COMPLETE) {
    setEnrichingState(false);
    const phoneNumbers = response?.request?.request_data?.phone_numbers;

    if (phoneNumbers?.length > 0) {
      const phoneNumber = phoneNumbers[0].sanitized_number;
      entity.value.phone_number = phoneNumber;
      entity.value[actionKey.value] = phoneNumber;

      eventBus.emit("table-row-updated", {
        phone_number: phoneNumber,
        action: quickActionsTypes.CALL,
        id: props.row.id,
      });

      message.success(
        `Found phone number for ${entity.value.first_name || ""} ${entity.value.last_name || ""}`,
      );
    } else {
      setEnrichingState(false);
      notFound.value = true;
    }
    stopPolling();
  }
};

const fetchRequestStatus = async () => {
  if (!requestID.value) return;

  try {
    const response = await DashboardService.getWaterfallEnrichmentStatus(
      requestID.value,
    );

    if (response?.success) {
      handleRequestStatus(response);
    }
  } catch (error) {
    handleRequestStatus(null, error);
  }
};

const handlePhoneResponse = (response, isSuccess) => {
  if (!isSuccess) {
    if (response?.message) message.error(response.message);
    return;
  }

  entity.value = response.lead;

  if (response.lead?.phone_number) {
    entity.value[actionKey.value] = response.lead.phone_number;
    setEnrichingState(false);
    eventBus.emit("table-row-updated", {
      ...response,
      action: quickActionsTypes.CALL,
      id: props.row.id,
    });
    return;
  }

  if (response?.request_id) {
    requestID.value = response.request_id;
    setEnrichingState(true);
    startPolling();
    return;
  }

  message.info("Phone number not available");
  setEnrichingState(false);
};

const addToLeads = async () => {
  setEnrichingState(true);

  const payload = {
    target: 1,
    list_id: null,
    sequence_id: null,
    selected_id: entity.value?.id,
    reveal_phone_number: true,
    reveal_email: false,
  };

  try {
    const response = await DashboardService.enrichAndAddLead(payload);

    handlePhoneResponse(response, response?.success);
  } catch (error) {
    handleApiError(error, message, (errorMessage) => {
      showAlreadyExistsMessage({ customMessage: errorMessage });
    });
  } finally {
    if (!requestID.value) {
      setEnrichingState(false);
    }
  }
};

const handleEnrichmentResponse = (response) => {
  if (response.data?.phone_number) {
    entity.value = response.data;
    entity.value[actionKey.value] = response.data.phone_number;
    eventBus.emit("table-row-updated", {
      ...response.data,
      action: quickActionsTypes.CALL,
      id: props.row.id,
    });
    setEnrichingState(false);
    return;
  }

  if (response?.requestId) {
    requestID.value = response.requestId;
    setEnrichingState(true);
    startPolling();
    return;
  }

  message.info("Phone number not available");
  setEnrichingState(false);
};

const enrichEntity = async () => {
  setEnrichingState(true);

  const source = props.row?.contact_id
    ? entityTypes.CONTACT
    : props.row?.lead_id
      ? entityTypes.LEAD
      : props.source;
  const entityId = props.row.lead_id || props.row.contact_id;

  try {
    const response = await enrichWithApollo(
      entityId,
      source,
      false,
      true,
      true,
    );
    handleEnrichmentResponse(response);
  } catch (error) {
    handleApiError(error, message, (errorMessage) => {
      showAlreadyExistsMessage({ customMessage: errorMessage });
    });
  } finally {
    if (!requestID.value) {
      setEnrichingState(false);
    }
  }
};

const handleEnrichingEvent = (state) => {
  isEnriching.value = state;
};

watch(
  () => props.row,
  (newRow) => {
    if (newRow) {
      entity.value = newRow;
      if (newRow.phone_number && entity.value) {
        entity.value[actionKey.value] = newRow.phone_number;
      }
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => entity.value?.[actionKey.value],
  (newValue) => {
    if (newValue) {
      notFound.value = false;
    }
  },
);

onBeforeUnmount(() => {
  stopPolling();
});

onMounted(() => {
  entity.value = props.row;
  eventBus.on(enrichingEventName.value, handleEnrichingEvent);

  if (props.row && props.row.phone_number && entity.value) {
    entity.value[actionKey.value] = props.row.phone_number;
  }
});

onUnmounted(() => {
  eventBus.off(enrichingEventName.value, handleEnrichingEvent);
});
</script>
